import { Link, matchPath, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Dialog,
  Drawer,
  HStack,
  Portal,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import SumsubWebSdk from '@sumsub/websdk-react';
// import { ReactComponent as Bell } from 'assets/icons/bell.svg';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import { ReactComponent as ExchangeMagix } from 'assets/icons/exchange_magix.svg';
import { ReactComponent as Gear } from 'assets/icons/gear.svg';
import { ReactComponent as MagixLogo } from 'assets/icons/magixLogo.svg';
import { ReactComponent as Menu } from 'assets/icons/menu.svg';
import { useAccess, useKycLevel2 } from 'hooks';
import { useTranslation } from 'localization';
import { routes } from 'statics';
import { UserLevel } from 'types';

import {
  IconButton,
  MenuList,
  ServiceDialogBody,
  ServiceDialogCloseTrigger,
  ServiceDialogHeader,
  serviceDialogShellProps,
  Spinner,
} from 'components';

type HeaderType = {
  username: string | null;
  level: UserLevel | undefined;
  isLoading: boolean;
};

/** Mobile header title: map pathname to `layout.header` keys (incl. wallet deposit/withdraw). */
function getHeaderTitleKey(pathname: string): string {
  const path = pathname.replace(/\/+$/, '') || '/';

  const routeMatches: Array<[string, string]> = [
    [routes.deposit.url, 'deposit'],
    [routes.withdraw.url, 'withdraw'],
    [routes.wallets.url, 'wallets'],
    [routes.dashboard.url, 'dashboard'],
    [routes.exchange.url, 'exchange'],
    [routes.transfer.url, 'transfer'],
    [routes.requests.url, 'requests'],
    [routes.settings.url, 'settings'],
  ];

  for (const [pattern, key] of routeMatches) {
    if (matchPath({ path: pattern, end: true }, path)) {
      return key;
    }
  }

  const first = path.split('/').find(Boolean);
  return first ?? 'dashboard';
}

const LEVEL_TYPE_MAP = {
  LEVEL0: 0,
  LEVEL1: 1,
  LEVEL2: 2,
  LD: 'Local Depositor',
  BROKER: 'BROKER',
} as const;

type HeaderUsernamePrefixProps = Readonly<{
  isLoading: boolean;
  username: string | null;
}>;

function HeaderUsernamePrefix({
  isLoading,
  username,
}: HeaderUsernamePrefixProps) {
  if (isLoading) {
    return (
      <Box display="inline">
        <Spinner size="sm" borderWidth="1px" />
      </Box>
    );
  }
  const namePrefix = username ? `${username} | ` : '';
  return <Box display="inline">{namePrefix}</Box>;
}

type HeaderLevelDisplayProps = Readonly<{
  isLoading: boolean;
  level: UserLevel | undefined;
}>;

function HeaderLevelDisplay({ isLoading, level }: HeaderLevelDisplayProps) {
  if (isLoading) {
    return (
      <Box display="inline">
        <Spinner size="sm" borderWidth="1px" />
      </Box>
    );
  }
  const levelDisplay =
    level !== undefined && level in LEVEL_TYPE_MAP
      ? LEVEL_TYPE_MAP[level as keyof typeof LEVEL_TYPE_MAP]
      : 0;
  return <Box display="inline">{levelDisplay}</Box>;
}

const Header: React.FC<HeaderType> = ({
  username = null,
  level = 'LEVEL0',
  isLoading,
}: HeaderType) => {
  const { t } = useTranslation('layout.header');
  const navigate = useNavigate();
  const location = useLocation();
  const access = useAccess();

  const { open, onOpen, onClose } = useDisclosure();
  const { data: kycData, mutateAsync: upgradeLevel2 } = useKycLevel2();
  const {
    open: isOpenLevel2,
    onOpen: onOpenLevel2,
    onClose: onCloseLevel2,
  } = useDisclosure();

  const headerTitleKey = getHeaderTitleKey(location.pathname);

  const ldLevel = (level || 'LD') === 'LD';

  const menuList = [
    {
      title: 'dashboard',
      link: '/dashboard',
      isActive: true,
    },
    { title: 'exchange', link: '/exchange', isActive: access.swapService },
    {
      title: 'transfer',
      link: '/transfer',
      isActive: access.transferService && !ldLevel,
    },
    { title: 'wallets', link: '/wallets', isActive: access.walletService },
    {
      title: 'requests',
      link: '/requests',
      isActive: ldLevel && access.requestLDService,
    },
    { title: 'settings', link: '/settings', isActive: true },
  ];

  const closeKycModalHandler = () => {
    navigate(0);
    onCloseLevel2();
  };

  return (
    <HStack
      justify="space-between"
      bg={{ base: 'none', lg: 'dark' }}
      borderRadius={{ base: 'none', lg: 'lg' }}
      py={{ base: '2', lg: '3' }}
      px="6"
      borderBottom={{ base: '1px solid', lg: 'none' }}
      borderColor="body2"
      w="full"
    >
      <HStack
        justify="space-between"
        w="full"
        display={{ base: 'flex', lg: 'none' }}
      >
        <HStack gap="4">
          <MagixLogo />
          <Text>{t(headerTitleKey)}</Text>
        </HStack>
        <HStack>
          {/* <IconButton ariaLabel="notification" icon={Bell} /> */}
          <IconButton
            ariaLabel="menu"
            icon={Menu}
            size="sm"
            onClick={() => onOpen()}
          />
        </HStack>
      </HStack>
      <HStack
        display={{ base: 'none', lg: 'flex' }}
        justify="space-between"
        w="full"
      >
        <HStack gap="2">
          <HStack>
            <Box display={{ base: 'none', lg: 'inline' }}>
              <HeaderUsernamePrefix isLoading={isLoading} username={username} />
            </Box>
            <Text>{t('level')} :</Text>
            <HeaderLevelDisplay isLoading={isLoading} level={level} />
            {level === 'LEVEL1' && (
              <Text
                color="primary"
                cursor="pointer"
                onClick={() => upgradeLevel2([]).then(() => onOpenLevel2())}
              >
                {t('upgrade')}
              </Text>
            )}
          </HStack>
        </HStack>
        <HStack gap="4">
          {/* <IconButton ariaLabel="notification" icon={Bell} /> */}
          <Link to={routes.settings.url}>
            <IconButton ariaLabel="setting" icon={Gear} />
          </Link>
        </HStack>
      </HStack>
      <Drawer.Root
        open={open}
        size="lg"
        onOpenChange={(e: { open: boolean }) => {
          if (!e.open) {
            onClose();
          }
        }}
      >
        <Portal>
          <Drawer.Backdrop />
          <Drawer.Positioner>
            <Drawer.Content asChild>
              <Box
                bg="sidebarbg"
                display="flex"
                flexDirection="column"
                height="100%"
              >
                <Drawer.Header px={4} pt={4} pb={3} borderBottom="none">
                  <HStack justify="space-between" align="center" w="full">
                    <Box
                      cursor="pointer"
                      onClick={() => globalThis.location.replace('/')}
                      css={{
                        '& svg': {
                          display: 'block',
                          maxWidth: '100px',
                          width: '100%',
                          height: 'auto',
                        },
                      }}
                    >
                      <ExchangeMagix />
                    </Box>
                    <IconButton
                      ariaLabel={t('closeMenu')}
                      icon={CloseIcon}
                      size="sm"
                      color="body2"
                      onClick={onClose}
                    />
                  </HStack>
                </Drawer.Header>
                <Drawer.Body
                  flex="1"
                  display="flex"
                  flexDirection="column"
                  minH={0}
                  px={4}
                  pt={2}
                  pb={6}
                >
                  <MenuList
                    list={menuList.filter((item) => item.isActive)}
                    closeDrawer={onClose}
                  />
                </Drawer.Body>
              </Box>
            </Drawer.Content>
          </Drawer.Positioner>
        </Portal>
      </Drawer.Root>
      <Dialog.Root
        closeOnInteractOutside={false}
        open={isOpenLevel2}
        size={{ base: 'full', lg: 'full' }}
        scrollBehavior="inside"
        onOpenChange={(e: { open: boolean }) => {
          if (!e.open) {
            closeKycModalHandler();
          }
        }}
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content {...serviceDialogShellProps({ maxW: 'full' })}>
              <ServiceDialogHeader>{t('upgradeToLvl2')}</ServiceDialogHeader>
              <ServiceDialogCloseTrigger />
              <ServiceDialogBody p={0} px={0} py={0} bg="sumsub">
                <Box px={0} bg="sumsub">
                  {kycData && (
                    <SumsubWebSdk
                      accessToken={kycData.token}
                      expirationHandler={() => closeKycModalHandler()}
                    />
                  )}
                </Box>
              </ServiceDialogBody>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </HStack>
  );
};
export default Header;
